import * as React from "react";
import AnyAssetFileUploader, {
  AwsLocation,
} from "../Common/AnyAssetFileUploader";
import PredictionFetchFormDialog from "./PredictionFetchFormDialog";
import { ISampleRecordCreationPayload } from "../../../types/sample";
import { CropTypes } from "../../../types/chemicals";
import UploadChemicalAnalysisDataDialog from "../UploadChemicalAnalysisDataDialog";
import { showSnackbar } from "../../../service/snackbar.service";
import TypeMismatchWarningDialog from "./TypeMismatchWarningDialog";
import { useTranslation } from "react-i18next";
import UploadSpectraDataDialog from "../UploadSpectraDataDialog";
import { IAdditiveEffectAlgorithm } from "../../../types/additiveAlgorithm";
import { SpectraDevice } from "../Common/SpectraDeviceSelect";
import { cropTypesMatch, resolveCropTypeName } from "../../../types/cropType";
import { useAppContext } from "../../../contexts/useStorageContext";
export default function UploadRawSpectraFileDialog({
  onClose,
}: {
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [openUploadFileDialog, setOpenUploadFileDialog] = React.useState(true);
  const [rawSpectraFileUrl, setRawSpectraFileUrl] = React.useState("");
  const [openPredictionFormDialog, setOpenPredictionFormDialog] =
    React.useState(false);
  const [openUploadChemDialog, setOpenUploadChemDialog] = React.useState(false);
  const [openUploadSpectraDialog, setOpenUploadSpectraDialog] =
    React.useState(false);
  const [parsedSampleRecords, setParsedSampleRecords] = React.useState<
    ISampleRecordCreationPayload[]
  >([]);
  const [appliedAdditives, setAppliedAdditives] = React.useState<
    IAdditiveEffectAlgorithm[]
  >([]);
  const [typeMismatchedRecords, setTypeMismatchedRecords] = React.useState<
    ISampleRecordCreationPayload[]
  >([]);
  const [openTypeMismatchWarningDialog, setOpenTypeMismatchWarningDialog] =
    React.useState(false);
  const [spectraDevice, setSpectraDevice] = React.useState<SpectraDevice | undefined>();
  const { state } = useAppContext();
  const cropTypes = state.cropTypes || [];

  const onFileUpload = (spectraFileUrl: string) => {
    setOpenUploadFileDialog(false);
    setRawSpectraFileUrl(spectraFileUrl || "");
    setOpenPredictionFormDialog(true);
  };

  return (
    <>
      {openUploadFileDialog ? (
        <AnyAssetFileUploader
          location={AwsLocation.PrivateAssets}
          directory={new Date().toISOString().substring(0, 7)}
          open={openUploadFileDialog}
          onClose={() => onClose()}
          onSave={(urls) => {
            if (urls?.[0]) {
              onFileUpload(urls[0]);
            }
          }}
          title={t("Upload Raw Spectra File")}
        />
      ) : null}
      {openPredictionFormDialog && rawSpectraFileUrl ? (
        <PredictionFetchFormDialog
          onCancelFile={() => {
            setOpenUploadFileDialog(true);
            setOpenPredictionFormDialog(false);
            setRawSpectraFileUrl("");
          }}
          rawSpectraFileUrl={rawSpectraFileUrl}
          onClose={() => onClose()}
          onSuccess={(parsed, onlySaveSpectraData, appliedAdditives, spectraDevice) => {
            setSpectraDevice(spectraDevice);
            if (!parsed?.length) {
              showSnackbar(
                "danger",
                t("No samples found in the spectra file"),
                t("Please check the spectra file and try again.")
              );
              return;
            }
            setParsedSampleRecords(parsed);
            setAppliedAdditives(appliedAdditives || []);
            if (
              parsed.some(
                (s) => !cropTypesMatch(s.type, s.predictedType, cropTypes)
              ) &&
              !onlySaveSpectraData
            ) {
              setTypeMismatchedRecords(
                parsed.filter(
                  (s) => !cropTypesMatch(s.type, s.predictedType, cropTypes)
                )
              );
              setOpenTypeMismatchWarningDialog(true);
            } else {
              if (onlySaveSpectraData) {
                setOpenUploadSpectraDialog(true);
              } else {
                setOpenUploadChemDialog(true);
              }
            }
          }}
        />
      ) : null}
      {openUploadChemDialog && parsedSampleRecords?.length ? (
        <UploadChemicalAnalysisDataDialog
          spectraDevice={spectraDevice}
          onClose={() => setOpenUploadChemDialog(false)}
          initialItems={parsedSampleRecords}
          initialFileName={
            rawSpectraFileUrl?.split("/")?.[
              rawSpectraFileUrl.split("/").length - 1
            ]
          }
          appliedAdditives={appliedAdditives}
        />
      ) : null}
      {openUploadSpectraDialog && parsedSampleRecords?.length ? (
        <UploadSpectraDataDialog
          onClose={() => setOpenUploadSpectraDialog(false)}
          initialItems={parsedSampleRecords}
          initialFileName={
            rawSpectraFileUrl?.split("/")?.[
              rawSpectraFileUrl.split("/").length - 1
            ]
          }
        />
      ) : null}
      {openTypeMismatchWarningDialog && typeMismatchedRecords?.length ? (
        <TypeMismatchWarningDialog
          isAll={parsedSampleRecords.length == typeMismatchedRecords.length}
          onClose={() => setOpenTypeMismatchWarningDialog(false)}
          records={typeMismatchedRecords}
          onExclude={() => {
            setOpenTypeMismatchWarningDialog(false);
            if (parsedSampleRecords.length == typeMismatchedRecords.length)
              return;
            setParsedSampleRecords(
              parsedSampleRecords.filter(
                (r) =>
                  !typeMismatchedRecords.some(
                    (t) => t.sampleNumber == r.sampleNumber
                  )
              )
            );
            setOpenUploadChemDialog(true);
          }}
          onIgnore={() => {
            setOpenTypeMismatchWarningDialog(false);
            setOpenUploadChemDialog(true);
          }}
          onAcceptPredicted={() => {
            setOpenTypeMismatchWarningDialog(false);
            setParsedSampleRecords(
              parsedSampleRecords.map((r) => {
                const mismatched = typeMismatchedRecords.find(
                  (t) => t.sampleNumber == r.sampleNumber
                );
                if (!mismatched?.predictedType) return r;
                return {
                  ...r,
                  type: (resolveCropTypeName(
                    mismatched.predictedType,
                    cropTypes
                  ) || mismatched.predictedType) as CropTypes,
                };
              })
            );
            setOpenUploadChemDialog(true);
          }}
        />
      ) : null}
    </>
  );
}
